import type { ApiKeyCreated } from '@servicetim/shared';
import { Badge, Input, Label } from '@servicetim/ui/components';
import { TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import { CopyButton } from '../common/copy-button';

/** The one and only time the API key secret is visible. */
export function SecretBox({ created }: { created: ApiKeyCreated }) {
  const id = useId();
  return (
    <div className="space-y-4" data-slot="secret-box">
      <div
        role="alert"
        className="flex gap-2 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 p-3 text-amber-900 dark:text-amber-300 text-sm"
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p>
          <span className="font-medium">This secret is shown once.</span> Copy it now and store it
          in your secret manager; the API only keeps a hash and cannot show it again.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-secret`}>Secret for {created.name}</Label>
        <div className="flex items-center gap-2">
          <Input
            id={`${id}-secret`}
            readOnly
            value={created.secret}
            className="font-mono text-xs"
            onFocus={(event) => event.currentTarget.select()}
          />
          <CopyButton value={created.secret} label="Copy secret" />
        </div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-fg-muted">Prefix</dt>
        <dd className="font-mono text-xs">{created.keyPrefix}</dd>
        <dt className="text-fg-muted">Scopes</dt>
        <dd className="flex flex-wrap gap-1">
          {created.scopes.map((scope) => (
            <Badge key={scope} variant="secondary">
              {scope}
            </Badge>
          ))}
        </dd>
        <dt className="text-fg-muted">Projects</dt>
        <dd>{created.projectIds ? `${created.projectIds.length} restricted` : 'All projects'}</dd>
      </dl>
    </div>
  );
}
